import React from 'react';
import { Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { reviewsData } from '../mockData';

const Reviews = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-10 h-10 fill-[#2F4538] text-[#2F4538]" />
              ))}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-4 font-poppins">
              5.0 Rating
            </h1>
            <p className="text-xl text-gray-700">
              Trusted by homeowners across Gurugram
            </p>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {reviewsData.map((review) => (
                <Card key={review.id} className="border-none shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className="flex items-center mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-6 h-6 fill-[#2F4538] text-[#2F4538]" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 italic leading-relaxed text-lg">
                      "{review.text}"
                    </p>
                    <div className="border-t border-gray-200 pt-4">
                      <p className="font-semibold text-[#2C2C2C] text-lg">{review.name}</p>
                      <p className="text-gray-500 text-sm mt-1">{review.date}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Clients Love Us */}
      <section className="py-20 bg-[#E8DCC4]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-12 font-poppins text-center">
              Why Clients Love Mayo Design
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  highlight: 'Complete Reconstruction',
                  detail: 'From structural changes to final styling, we handle every aspect with expertise.'
                },
                {
                  highlight: 'Stunning Design',
                  detail: 'Beautiful, thoughtful interiors that reflect your personality and lifestyle.'
                },
                {
                  highlight: 'Bespoke Furniture',
                  detail: 'Custom-crafted pieces that perfectly fit your space and needs.'
                },
                {
                  highlight: 'Professional Service',
                  detail: 'Responsive communication, transparent processes, and exceptional value.'
                }
              ].map((item, index) => (
                <Card key={index} className="border-none shadow-md">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-[#2F4538] mb-2 font-poppins">
                      {item.highlight}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{item.detail}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Link */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-[#2C2C2C] mb-4 font-poppins">
              See What Our Clients Say on Google
            </h3>
            <p className="text-gray-700 mb-6">
              Check out our genuine reviews from satisfied homeowners who trusted us with their dream homes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button asChild variant="outline" className="border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#F5F1E8]">
                <a
                  href="https://www.google.com/maps/place/Mayo+Design"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Google Reviews
                </a>
              </Button>
              <span className="text-gray-500">or call us at</span>
              <a href="tel:+919910837533" className="text-[#2F4538] font-bold text-xl hover:underline">
                +91 99108 37533
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2F4538] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-poppins">
            Ready to Experience Mayo Design?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our satisfied clients and let us create something extraordinary for your home.
          </p>
          <Button asChild size="lg" className="bg-white text-[#2F4538] hover:bg-gray-100 transition-all hover:scale-105">
            <Link to="/contact">Start Your Project</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Reviews;